import { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip,
} from '@mui/material';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../../api/hooks/useProjects';
import { useCreateOffenerPunkt } from '../../api/hooks/useOffenePunkte';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { useNotifications } from '../../context/NotificationContext';
import { KATEGORIEN } from '../../pages/projects/tabs/OffenePunkteTab';
import { apiErrorMessage } from '../../lib/apiErrorMessage';
import { AppDialogTitle } from './AppDialogTitle';

interface ProjectOption {
  id: string;
  name: string;
  project_no: string | null;
}

/**
 * Globale Schnellanlage fuer einen Offenen Punkt — von ueberall in der TopBar
 * erreichbar. Ein Offener Punkt ist projekt-gebunden, darum enthaelt der
 * Dialog eine Projekt-Auswahl (Default: das aktuell aktive Projekt, falls
 * vorhanden; sonst muss eines gewaehlt werden).
 */
export function QuickCreateOffenerPunkt() {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { activeProjectId } = useActiveProject();

  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [titel, setTitel] = useState('');
  const [kategorie, setKategorie] = useState('');

  const { data: projectsResp } = useProjects({ limit: 100 });
  const projects = ((projectsResp as unknown as { data?: ProjectOption[] } | undefined)?.data ?? []);

  const createMut = useCreateOffenerPunkt(projectId);

  useEffect(() => {
    if (!open) return;
    setProjectId(activeProjectId ?? '');
    setTitel('');
    setKategorie('');
  }, [open, activeProjectId]);

  const canSave = projectId.trim().length > 0 && titel.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await createMut.mutateAsync({
        titel: titel.trim(),
        kategorie: kategorie || null,
        faellig: null,
        status: 'Offen',
        beschreibung: null,
        verantwortlich: null,
        meeting_id: null,
        quelle: 'offene-punkte',
      });
      notify(t('offenePunkte.quickCreateSuccess', 'Offener Punkt angelegt'), 'success');
      setOpen(false);
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
  };

  return (
    <>
      <Tooltip title={t('offenePunkte.quickCreate', 'Offenen Punkt anlegen')}>
        <IconButton size="small" onClick={() => setOpen(true)} aria-label={t('offenePunkte.quickCreate', 'Offenen Punkt anlegen')}>
          <PostAddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t('offenePunkte.quickCreate', 'Offenen Punkt anlegen')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>{t('offenePunkte.projekt', 'Projekt')}</InputLabel>
              <Select
                value={projectId}
                label={t('offenePunkte.projekt', 'Projekt')}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.project_no ? `${p.project_no} – ${p.name}` : p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('offenePunkte.titel', 'Titel')}
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>{t('offenePunkte.kategorie', 'Kategorie')}</InputLabel>
              <Select value={kategorie} label={t('offenePunkte.kategorie', 'Kategorie')} onChange={(e) => setKategorie(e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {KATEGORIEN.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={!canSave || createMut.isPending}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
